import { useState } from "react";
import {
  GoogleAuthProvider,
  signInWithPopup,
  UserCredential,
} from "firebase/auth";
import { auth } from "@/api/utils/firebase";
import toast from "react-hot-toast";
import {
  setAuthToken,
  getPendingEnrollCourseId,
  clearPendingEnrollCourseId,
} from "../utils/session";
import { enrollCourse } from "../query/courses";
import apiClient from "../config";

interface LocationData {
  country: string;
  countryState: string;
}

export const useGoogleAuth = () => {
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleAuth = async (
    type: "signin" | "signup",
    location?: LocationData,
  ) => {
    setIsLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const result: UserCredential = await signInWithPopup(auth, provider);
      const user = result.user;

      if (!user.email) {
        throw new Error("Email is required for authentication");
      }

      const pendingCourseId = getPendingEnrollCourseId();

      if (type === "signup") {
        if (!location) {
          throw new Error("Location is required for signup");
        }

        const displayName = user.displayName || "Unknown User";
        const nameParts = displayName.split(" ");
        const firstName = nameParts[0];
        const lastName =
          nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
        const middleName =
          nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : "";

        if (pendingCourseId) {
          const signupData = new FormData();
          signupData.append("email", user.email);
          signupData.append("firstName", firstName);
          signupData.append("lastName", lastName);
          signupData.append("middleName", middleName);
          signupData.append("uid", user.uid);
          signupData.append("country", location.country);
          signupData.append("countryState", location.countryState);

          const response = await apiClient.post(
            `/student/createStudentAndCourseGoogle/${pendingCourseId}/0`,
            signupData,
          );

          // Set token so middleware allows /dashboard access
          if (response.data?.token) {
            setAuthToken(response.data.token);
          }

          clearPendingEnrollCourseId();
          toast.success("Account created and enrolled in course successfully!");
        } else {
          const signupData = new FormData();
          signupData.append("email", user.email);
          signupData.append("firstName", firstName);
          signupData.append("lastName", lastName);
          signupData.append("middleName", middleName);
          signupData.append("uid", user.uid);
          signupData.append("country", location.country);
          signupData.append("countryState", location.countryState);

          const response = await apiClient.post(
            "/student/createStudentGoogle",
            signupData,
          );

          // Set token so middleware allows /dashboard access
          if (response.data?.token) {
            setAuthToken(response.data.token);
          }

          toast.success("Account created successfully!");
        }
      } else {
        // Handle sign in
        const signinData = {
          email: user.email,
          uid: user.uid,
        };

        const response = await apiClient.post(
          "/student/authenticate",
          signinData,
        );

        if (response.data?.token) {
          setAuthToken(response.data.token);
        }

        toast.success("Signed in successfully!");

        if (pendingCourseId) {
          try {
            await enrollCourse(pendingCourseId, null);
            clearPendingEnrollCourseId();
            toast.success("Successfully enrolled in the course!");
            // Full reload so middleware receives the fresh cookie
            window.location.href = "/dashboard/courses";
            return user;
          } catch (enrollError) {
            console.error("Failed to auto-enroll in course:", enrollError);
          }
        }
      }

      // Full page navigation so the browser sends the newly set cookie
      // to the middleware — router.push does not guarantee this in production
      window.location.href = "/dashboard";
      return user;
    } catch (error: any) {
      if (error.response?.status === 400) {
        if (
          type === "signin" &&
          error.response.data === "User does not exist"
        ) {
          const shouldSignUp = confirm(
            "User does not exist. Would you like to Sign Up?",
          );
          if (shouldSignUp) {
            window.location.href = "/sign-up";
          }
        } else {
          toast.error(error.response.data);
        }
      } else if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("Authentication failed. Please try again.");
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = () => handleGoogleAuth("signin");
  const signUpWithGoogle = (location: LocationData) =>
    handleGoogleAuth("signup", location);

  return {
    signInWithGoogle,
    signUpWithGoogle,
    isLoading,
  };
};
